import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Chip,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TextFieldProps,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FileUpload as FileUploadIcon,
} from '@mui/icons-material';
import moment, { Moment } from 'moment';
import AdapterMoment from '@mui/lab/AdapterMoment';

import { useAppSelector } from '../../../../../hooks';
import {
  getMeetingRequestsState,
  IMeetingRequest,
  resetMeetingRequestListData,
  TMeetingRequestStatus,
  TMeetingRequestType,
  updateMeetingRequestListItem,
} from '../../../../../store/V2/MeetingRequestsSlice';
import {
  TMeetingRequestStatusOption,
  TMeetingRequestTypeOption,
  meetingRequestStatusOptions,
  meetingRequestTypeOptions,
} from '../../../../../constants';
import useTable, {
  defaultRowsPerPageOptions,
  TUseTableState,
} from '../../../../../components/V2/Table/type1/useTable';
import TableHeadCustom, {
  THeadLabelItem,
} from '../../../../../components/V2/Table/type1/TableHeadCustom';
import {
  getMeetingRequests,
  TGetMeetingRequests,
} from '../../../../../store/V2/MeetingRequestsSlice/api';
import RescheduledOrCancelledTableRow from './components/RescheduledOrCancelledTableRow';
import TablePaginationActions from '../../../../../components/V2/Table/type1/TablePaginationActions';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../components/Inputs';
import TableNoData from '../../../../../components/V2/Empty/TableNoData';
import TableLoader from '../../../../../components/Loaders/TableLoader';
import {
  capitalizeFirstLetter,
  checkUserPermission,
  prepareRescheduleRequestCSVExportData,
} from '../../../../../helper';
import {
  getLessonHistoryState,
  updateLessonHistoryCommonFilterData,
} from '../../../../../store/V2/LessonHistorySlice';
import PenaltyAppealModal from './modals/PenaltyAppealModal';
import ViewRequestModal from './modals/ViewRequestModal';
import { getAuthState } from '../../../../../store/AuthSlice';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../store/V2/RolesSlice';
import TutorPaymentDetailsCard from './components/TutorPaymentDetailsCard';
import {
  DarkButton,
  defaultButtonStyles,
} from '../../../../../components/V2/Buttons';

type TFilterData = {
  limit: number;
  offset: number;
  sortKey: string;
  sortValue: number;
  startDate?: string;
  endDate?: string;
  status?: string;
  type?: TMeetingRequestType;
};

type TState = {
  startDate: Moment | null;
  endDate: Moment | null;
  selectedStatus?: TMeetingRequestStatusOption | null;
  selectedType: TMeetingRequestTypeOption | null;
};

const textFieldProps: TextFieldProps = {
  sx: {
    backgroundColor: '#ffffff',
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const defaultListLimit = 30;
const defaultOrderBy = 'createdAt';
const defaultOrder = 'desc';
const defaultTableData: TUseTableState = {
  defaultOrderBy,
  defaultOrder,
  defaultDense: false,
  defaultCurrentPage: 0,
  defaultRowsPerPage: defaultRowsPerPageOptions[0],
  defaultSelected: [],
};
const defaultStartDateMoment = moment().subtract(60, 'days');
const defaultEndDateMoment = moment().add(90, 'days');

const defaultState: TState = {
  startDate: defaultStartDateMoment.clone(),
  endDate: defaultEndDateMoment.clone(),
  selectedStatus: null,
  selectedType: null,
};

const defaultFilterData: TFilterData = {
  limit: defaultListLimit,
  offset: 0,
  sortKey: defaultOrderBy,
  sortValue: defaultOrder === 'desc' ? -1 : 1,
  startDate: defaultStartDateMoment.startOf('day').toISOString(),
  endDate: defaultEndDateMoment.endOf('day').toISOString(),
};

const tableHeaderLabels: THeadLabelItem[] = [
  {
    id: 'student',
    label: 'Student Name',
    sortable: false,
    minWidth: 108,
    maxWidth: 108,
  },
  {
    id: 'createdAt',
    label: 'Requested At',
    sortable: true,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'start',
    label: 'Schedule Time',
    sortable: true,
    minWidth: 180,
    maxWidth: 180,
    badgeElement: (
      <>
        &nbsp;
        <Chip
          size="small"
          label="Old"
          sx={{
            backgroundColor: '#94A3B8',
            color: '#1E293B',
            fontSize: '10px',
            fontWeight: 500,
            borderRadius: '3px',
            p: '3px 6px 3px 6px',
          }}
        />
      </>
    ),
  },
  {
    id: 'newStart',
    label: 'Schedule Time',
    sortable: true,
    minWidth: 180,
    maxWidth: 180,
    badgeElement: (
      <>
        &nbsp;
        <Chip
          size="small"
          label="New"
          sx={{
            backgroundColor: '#3498DB',
            color: '#F8FAFC',
            fontSize: '10px',
            fontWeight: 500,
            borderRadius: '3px',
            p: '3px 6px 3px 6px',
          }}
        />
      </>
    ),
  },
  {
    id: 'rescheduleType',
    label: 'Reschedule Type',
    sortable: true,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'status',
    label: 'Status',
    sortable: true,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'tutorPenalty',
    label: 'Tutor Penalty',
    sortable: false,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'tutorPenaltyAmount',
    label: 'Tutor Penalty Amount',
    sortable: false,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'studentPenalty',
    label: 'Student Penalty',
    sortable: false,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'studentPenaltyAmount',
    label: 'Student Penalty Amount',
    sortable: false,
    minWidth: 120,
    maxWidth: 120,
  },
  {
    id: 'reason',
    label: 'Reason',
    sortable: false,
    minWidth: 200,
    maxWidth: 200,
  },
  {
    id: 'action',
    label: 'Action',
    sortable: false,
    align: 'right',
  },
];

const RescheduledOrCancelledTab: FunctionComponent = () => {
  const dispatch = useDispatch();

  // Store
  const authState = useAppSelector(getAuthState);
  const meetingRequestsState = useAppSelector(getMeetingRequestsState);
  const lessonHistoryState = useAppSelector(getLessonHistoryState);

  // State
  const tableData = useTable(defaultTableData);
  const [state, setState] = useState({ ...defaultState });
  const [filterData, setFilterData] = useState({ ...defaultFilterData });
  const [
    meetingRequestAppealModalData,
    setMeetingRequestAppealModalData,
  ] = useState<{
    open: boolean;
    meetingRequest?: IMeetingRequest;
  }>({
    open: false,
  });

  const [
    viewMeetingRequestModalData,
    setViewMeetingRequestModalData,
  ] = useState<{
    open: boolean;
    defaultTabIndex?: number;
    meetingRequest?: IMeetingRequest;
  }>({ open: false });

  const { lessonHistoryCommonFilterData } = lessonHistoryState;

  const authUser = authState.user;
  const hasTutorAmountDataPermission = checkUserPermission(authUser, {
    lessonHistoryCompletedTab: [
      USER_PERMISSION_INDIVIDUALS.LESSON_HISTORY_COMPLETED_TAB_TUTOR_AMOUNT,
    ],
  });

  let MeetingRequestsList: IMeetingRequest[] = [];
  let totalCount = 0;

  if (meetingRequestsState.meetingRequestListData) {
    totalCount = meetingRequestsState.meetingRequestListData.totalDocs || 0;

    if (
      meetingRequestsState.meetingRequestListData.list &&
      meetingRequestsState.meetingRequestListData.list.length
    ) {
      MeetingRequestsList = meetingRequestsState.meetingRequestListData.list;
    }
  }

  useEffect(() => {
    getInitialZoomMeetingRequestsList();

    return () => {
      dispatch(resetMeetingRequestListData());
    };
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const getInitialZoomMeetingRequestsList = () => {
    const initialFilterData: TFilterData = {
      ...defaultFilterData,
    };

    const newStateData: Partial<TState> = {};

    if (lessonHistoryCommonFilterData) {
      if (lessonHistoryCommonFilterData.startDate) {
        newStateData.startDate = moment(
          lessonHistoryCommonFilterData.startDate
        );

        initialFilterData.startDate = moment(
          lessonHistoryCommonFilterData.startDate
        )
          .startOf('day')
          .toISOString();
      } else if (lessonHistoryCommonFilterData.startDateCleared) {
        newStateData.startDate = null;
        initialFilterData.startDate = undefined;
      }

      if (lessonHistoryCommonFilterData.endDate) {
        newStateData.endDate = moment(lessonHistoryCommonFilterData.endDate);

        initialFilterData.endDate = moment(
          lessonHistoryCommonFilterData.endDate
        )
          .endOf('day')
          .toISOString();
      } else if (lessonHistoryCommonFilterData.endDateCleared) {
        newStateData.endDate = null;
        initialFilterData.endDate = undefined;
      }
    }

    getZoomMeetingRequestsList({ ...initialFilterData });
    updateStateAttributes({ ...newStateData });
  };

  const getZoomMeetingRequestsList = async (
    filterDataAttributes: TFilterData
  ) => {
    const apiData: TGetMeetingRequests = {
      limit: filterDataAttributes.limit,
      offset: filterDataAttributes.offset,
    };

    if (hasTutorAmountDataPermission) {
      apiData.includeTutorAmountData = true;
    }

    if (filterDataAttributes.sortKey) {
      apiData.sortKey = filterDataAttributes.sortKey;
    }

    if (filterDataAttributes.sortValue) {
      apiData.sortValue = filterDataAttributes.sortValue;
    }

    if (filterDataAttributes.startDate) {
      apiData.startDate = filterDataAttributes.startDate;
    }

    if (filterDataAttributes.endDate) {
      apiData.endDate = filterDataAttributes.endDate;
    }

    if (filterDataAttributes.type) {
      apiData.type = filterDataAttributes.type;
    }

    if (filterDataAttributes.status) {
      apiData.statuses = [
        filterDataAttributes.status,
      ] as TMeetingRequestStatus[];
    }

    dispatch(getMeetingRequests({ ...apiData }));

    setFilterData({ ...filterDataAttributes });
  };

  const handleSort = (id: string) => {
    let newSortValue = 1;

    if (filterData.sortKey === id && filterData.sortValue === 1) {
      newSortValue = -1;
    }

    getZoomMeetingRequestsList({
      ...filterData,
      sortValue: newSortValue,
      sortKey: id,
    });

    tableData.onSort(id);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => {
    getZoomMeetingRequestsList({
      ...filterData,
      offset: page * filterData.limit,
    });

    tableData.onChangePage(event, page);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    getZoomMeetingRequestsList({
      ...filterData,
      offset: 0,
      limit: parseInt(event.target.value, 10),
    });

    tableData.onChangeRowsPerPage(event);
  };

  const refreshList = () => {
    getZoomMeetingRequestsList({ ...filterData });
  };

  const onExport = () => {
    const link = document.createElement('a');
    const filename = 'lesson-history-export.csv';

    let csv = prepareRescheduleRequestCSVExportData({
      list: MeetingRequestsList,
    });

    if (csv == null) return;

    if (!csv.match(/^data:text\/csv/i)) {
      csv = `data:text/csv;charset=utf-8,${csv}`;
    }

    link.setAttribute('href', encodeURI(csv));
    link.setAttribute('download', filename);
    link.click();
  };

  return (
    <div>
      <div style={{ padding: '20px' }}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Grid
            container
            alignItems="center"
            columnSpacing="16px"
            rowSpacing="20px"
          >
            <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
              <FormControl fullWidth>
                <DatePicker
                  clearable
                  value={state.startDate}
                  label="Start Date"
                  inputFormat="YYYY-MMM-DD"
                  onChange={(date) => {
                    updateStateAttributes({
                      startDate: date,
                    });

                    getZoomMeetingRequestsList({
                      ...filterData,
                      startDate: date
                        ? date.clone().startOf('day').toISOString()
                        : undefined,
                    });

                    dispatch(
                      updateLessonHistoryCommonFilterData({
                        startDate: date ? date.toISOString() : undefined,
                        startDateCleared: date ? undefined : true,
                      })
                    );
                  }}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      placeholder="Start Date"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
              <FormControl fullWidth>
                <DatePicker
                  clearable
                  value={state.endDate}
                  label="End Date"
                  inputFormat="YYYY-MMM-DD"
                  onChange={(date) => {
                    updateStateAttributes({
                      endDate: date,
                    });

                    getZoomMeetingRequestsList({
                      ...filterData,
                      endDate: date
                        ? date.clone().endOf('day').toISOString()
                        : undefined,
                    });

                    dispatch(
                      updateLessonHistoryCommonFilterData({
                        endDate: date ? date.toISOString() : undefined,
                        endDateCleared: date ? undefined : true,
                      })
                    );
                  }}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      placeholder="End Date"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
              <FormControl fullWidth>
                <Autocomplete
                  size="small"
                  value={state.selectedStatus}
                  options={meetingRequestStatusOptions}
                  onChange={(e, value) => {
                    updateStateAttributes({
                      selectedStatus: value,
                    });

                    getZoomMeetingRequestsList({
                      ...filterData,
                      status: value ? value.value : undefined,
                    });
                  }}
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.value
                  }
                  getOptionLabel={(option) =>
                    capitalizeFirstLetter(option.label.toLowerCase())
                  }
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Status"
                      placeholder="Filter by Status"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
              <FormControl fullWidth>
                <Autocomplete
                  size="small"
                  value={state.selectedType}
                  options={meetingRequestTypeOptions}
                  onChange={(e, value) => {
                    updateStateAttributes({
                      selectedType: value,
                    });

                    getZoomMeetingRequestsList({
                      ...filterData,
                      type: value ? value.value : undefined,
                    });
                  }}
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  isOptionEqualToValue={(option, value) =>
                    option.label === value.value
                  }
                  getOptionLabel={(option) =>
                    capitalizeFirstLetter(option.label.toLowerCase())
                  }
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Request Type"
                      placeholder="Request Type"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
              <Tooltip title="Refresh List">
                <IconButton onClick={refreshList}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </div>

      <Collapse
        in={
          hasTutorAmountDataPermission &&
          Boolean(meetingRequestsState.meetingRequestListData)
        }
      >
        <TutorPaymentDetailsCard
          startDate={state.startDate}
          endDate={state.endDate}
        />
      </Collapse>

      {MeetingRequestsList.length ? (
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          p="0 20px 20px 20px"
        >
          <DarkButton
            variant="contained"
            color="inherit"
            onClick={onExport}
            startIcon={<FileUploadIcon />}
            sx={{ ...defaultButtonStyles }}
          >
            Export
          </DarkButton>
        </Stack>
      ) : null}

      <TableContainer
        sx={{ position: 'relative', maxHeight: 'calc(100vh - 250px)' }}
      >
        <Table stickyHeader size={tableData.dense ? 'small' : 'medium'}>
          <TableHeadCustom
            order={tableData.order}
            orderBy={tableData.orderBy}
            headLabels={tableHeaderLabels}
            rowCount={0}
            numSelected={tableData.selected.length}
            onSort={handleSort}
          />
          <TableBody>
            {MeetingRequestsList.map((row) => {
              return (
                <RescheduledOrCancelledTableRow
                  key={row._id}
                  requestData={row}
                  onView={() =>
                    setViewMeetingRequestModalData({
                      open: true,
                      defaultTabIndex: 0,
                      meetingRequest: row,
                    })
                  }
                  onAppeal={() =>
                    setMeetingRequestAppealModalData({
                      open: true,
                      meetingRequest: row,
                    })
                  }
                  onStudentsClick={() =>
                    setViewMeetingRequestModalData({
                      open: true,
                      defaultTabIndex: 1,
                      meetingRequest: row,
                    })
                  }
                />
              );
            })}
            {meetingRequestsState.status !== 'loading' &&
            !MeetingRequestsList.length ? (
              <TableNoData title="No records found" />
            ) : null}
            {meetingRequestsState.status === 'loading' &&
            !MeetingRequestsList.length ? (
              <TableLoader />
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={defaultRowsPerPageOptions}
          component="div"
          count={totalCount}
          rowsPerPage={tableData.rowsPerPage}
          page={tableData.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleRowsPerPageChange}
          ActionsComponent={TablePaginationActions}
        />

        <FormControlLabel
          label="Dense"
          sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
          control={
            <Switch
              checked={tableData.dense}
              onChange={tableData.onChangeDense}
            />
          }
        />
      </Box>
      {/* Modals */}
      {meetingRequestAppealModalData.open &&
      meetingRequestAppealModalData.meetingRequest ? (
        <PenaltyAppealModal
          open={meetingRequestAppealModalData.open}
          onClose={() => setMeetingRequestAppealModalData({ open: false })}
          meetingRequest={meetingRequestAppealModalData.meetingRequest}
          onSubmitted={(data) => {
            if (data && data.zoomMeetingRequest) {
              dispatch(
                updateMeetingRequestListItem({
                  _id: data.zoomMeetingRequest._id,
                  attributes: {
                    isPenaltyAppealRequestReceived:
                      data.zoomMeetingRequest.isPenaltyAppealRequestReceived,
                  },
                })
              );
            }

            setMeetingRequestAppealModalData({ open: false });
          }}
        />
      ) : null}
      {viewMeetingRequestModalData.open &&
      viewMeetingRequestModalData.meetingRequest ? (
        <ViewRequestModal
          open={viewMeetingRequestModalData.open}
          onClose={() => setViewMeetingRequestModalData({ open: false })}
          zoomRequestId={viewMeetingRequestModalData.meetingRequest._id}
          defaultTabIndex={viewMeetingRequestModalData.defaultTabIndex}
        />
      ) : null}
    </div>
  );
};

export default RescheduledOrCancelledTab;
